import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerAPI } from '../api/axios';
import Button from '../components/Button';
import './CustomerListPage.css';

const CustomerListPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 고객 목록 불러오기
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getAll();
      setCustomers(response.data);
      setError(null);
    } catch (err) {
      setError('고객 목록을 불러오는데 실패했습니다.');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // 검색 필터링
  const filteredCustomers = customers.filter(customer => 
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  );

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="customer-list-container">
        <div className="loading">고객 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-list-container">
        <div className="error-message">{error}</div>
        <Button onClick={fetchCustomers} variant="primary" size="medium">
          다시 시도
        </Button>
      </div>
    );
  }

  return (
    <div className="customer-list-container">
      {/* 헤더 */}
      <header className="customer-list-header">
        <h1>ANEW Aesthetic</h1>
        <p className="subtitle">고객 관리</p>
      </header>

      {/* 검색 및 통계 */}
      <div className="search-stats-section">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">전체 고객</div>
            <div className="stat-value">{customers.length}명</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">신규 고객</div>
            <div className="stat-value">
              {customers.filter(c => c.visitCount === 0).length}명
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">단골 고객</div>
            <div className="stat-value">
              {customers.filter(c => c.visitCount > 0).length}명
            </div>
          </div>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="이름 또는 전화번호로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* 고객 목록 테이블 */}
      <div className="table-container">
        <table className="customer-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>이름</th>
              <th>전화번호</th>
              <th>방문 횟수</th>
              <th>노쇼 횟수</th>
              <th>피부 타입</th>
              <th>가입일</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? '검색 결과가 없습니다.' : '등록된 고객이 없습니다.'}
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr 
                  key={customer.id} 
                  className="customer-row clickable-row"
                  onClick={() => navigate(`/admin/customers/${customer.id}`)}
                  title="클릭하여 상세 정보 보기"
                >
                  <td>{customer.id}</td>
                  <td className="customer-name">
                    <strong>{customer.name}</strong>
                    {customer.tags && (
                      <div className="customer-tags-preview">
                        {customer.tags.split(',').slice(0, 3).map((tag, i) => (
                          <span key={i} className="mini-tag">{tag.trim()}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>{customer.phone}</td>
                  <td>
                    <span className={`badge ${customer.visitCount > 0 ? 'badge-success' : 'badge-new'}`}>
                      {customer.visitCount}회
                    </span>
                  </td>
                  <td>
                    {customer.noShowCount > 0 ? (
                      <span className="badge badge-warning">{customer.noShowCount}회</span>
                    ) : (
                      <span className="badge badge-gray">0회</span>
                    )}
                  </td>
                  <td>{customer.skinType || '-'}</td>
                  <td className="date-cell">{formatDate(customer.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 새로고침 버튼 */}
      <div className="action-buttons">
        <Button onClick={fetchCustomers} variant="outline" size="medium">
          🔄 새로고침
        </Button>
      </div>
    </div>
  );
};

export default CustomerListPage;
